import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import Helmet from 'react-helmet'
import styled from 'styled-components'

import { filterRelatedAlerts } from '../utils/alerts'
import getImageUrl from '../utils/getImageUrl'
import { Container, Relative, Page } from '../components/common'
import MapGL from '../components/MapGL'
import Meta from '../components/Meta'
import Header from '../components/Header'
import TextCard from '../components/TextCard'
import SectionTitle from '../components/SectionTitle'
import { FeatureCard } from '../components/FeatureGrid'
import CardContainer from '../components/CardContainer'
import SpeciesCard from '../components/SpeciesCard'
import AlertCard from '../components/AlertCard'
import NoMatch from '../views/NoMatch'
import {
  IconDuration,
  IconLength,
  IconClassification
} from '../components/icons'

const Details = styled.div`
  padding: 1.4rem 2rem;
  background: white;
  display: flex;
`
const Detail = styled.div`
  text-align: center;
  width: 100%;
  flex: 1;
  margin-bottom: 0;
`
const DetailHeading = styled.h6`
  margin-bottom: 0rem;
`
const DetailContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  & > *:nth-child(1) {
    margin-right: 1rem;
  }
`

const TrackSingle = ({
  tracks,
  match,
  locations,
  alerts,
  linkContentRelations,
  route
}) => {
  const item = tracks.find(item => item.fields.slug === match.params.slug)
  if (!item) return <NoMatch route={route} />

  const highlights = locations.filter(
    location =>
      location.fields.tracks &&
      location.fields.tracks.find(track => track.sys.id === item.sys.id)
  )

  const {
    title,
    mainImage,
    images = [],
    trackLength,
    trackClassification,
    trackDuration,
    description,
    speciesLucky,
    speciesCommon,
    geoBounds
  } = item.fields

  const isPublished = item => !!item.fields

  return (
    <Page>
      <Helmet title={title} />
      <Meta
        title={title}
        description={description}
        url={`https://discoverspringbrook.com/walks/${item.fields.slug}`}
      />
      <Header title={title} images={[mainImage, ...images]} slider large />

      <Container desktopOnly>
        <Details>
          {trackLength && (
            <Detail>
              <DetailHeading>Distance</DetailHeading>
              <DetailContent>
                <IconLength /> {trackLength} km
              </DetailContent>
            </Detail>
          )}
          {trackDuration && (
            <Detail>
              <DetailHeading>Duration</DetailHeading>
              <DetailContent>
                <IconDuration /> {trackDuration} hours
              </DetailContent>
            </Detail>
          )}
          {trackClassification && (
            <Detail>
              <DetailHeading>Difficulty</DetailHeading>
              <DetailContent>
                <IconClassification /> {trackClassification}
              </DetailContent>
            </Detail>
          )}
        </Details>

        {geoBounds && (
          <Link
            to={{
              pathname: '/map',
              search: `?bounds=${geoBounds}`
            }}
          >
            <Relative>
              <MapGL inset geoBounds={geoBounds} />
            </Relative>
          </Link>
        )}

        {description && (
          <TextCard content={linkContentRelations(description)} />
        )}

        {filterRelatedAlerts({ alerts, doc: item }).map(alert => (
          <AlertCard key={`AlertCard-${alert.id}`} {...alert} />
        ))}

        {!!highlights.length && (
          <Fragment>
            <SectionTitle
              title='Highlights'
              subtitle={`What to see on ${title}`}
            />

            <CardContainer>
              {highlights.map(({ fields, sys }) => (
                <FeatureCard
                  key={sys.id}
                  title={fields.title}
                  to={`/locations/${fields.slug}`}
                  image={getImageUrl(fields.mainImage)}
                />
              ))}
            </CardContainer>
          </Fragment>
        )}

        {!!speciesCommon && (
          <Fragment>
            <SectionTitle
              title='What To Expect'
              subtitle='Species commonly found on this track'
            />
            <CardContainer>
              {speciesCommon
                .filter(isPublished)
                .map(item => <SpeciesCard key={item.sys.id} item={item} />)}
            </CardContainer>
          </Fragment>
        )}

        {!!speciesLucky && (
          <Fragment>
            <SectionTitle
              title={`If you're lucky`}
              subtitle='Keep your eye out for these species'
            />
            <CardContainer>
              {speciesLucky
                .filter(isPublished)
                .map(item => <SpeciesCard key={item.sys.id} item={item} />)}
            </CardContainer>
          </Fragment>
        )}
      </Container>
    </Page>
  )
}

TrackSingle.propTypes = {
  tracks: PropTypes.array.isRequired,
  match: PropTypes.object.isRequired
}

export default TrackSingle



// WEBPACK FOOTER //
// ./src/views/TrackSingle.js